import { useContractMutation, useContractQuery, useInvalidateContract } from "@amtana/browser";
import type { MutationResult, QueryResult } from "@amtana/browser";
import { vaultTrpcContract } from "@amtana/vault-contract";
import type {
  CheckCodexSignInInput,
  CodexSignInCheck,
  DeleteVaultSecretInput,
  FinishClaudeSignInInput,
  HarnessLogin,
  HarnessLogins,
  HarnessSignIn,
  RemoveHarnessLoginInput,
  StartHarnessSignInInput,
  ListVaultSecretsOutput,
  Ok,
  SaveVaultLoginInput,
  SaveVaultSecretInput,
  VaultSecret,
} from "@amtana/vault-contract";

// The organization's vault (ADR-023 §4): its secrets' names, placeholders, hosts and agents, never
// a value; saving one (the value goes in once), saving a website login, and deleting one. And the
// member's own Claude or Codex login (§7): whether they have one, signing in, and removing it;
// never a token.

/** Where the Secrets screen lives; another module links to it with this, never by hand. */
export const VAULT_PATH = "/settings/vault";

export function useVaultSecrets(): QueryResult<ListVaultSecretsOutput> {
  return useContractQuery(vaultTrpcContract, "list", { input: {} });
}

export function useHarnessLogins(): QueryResult<HarnessLogins> {
  return useContractQuery(vaultTrpcContract, "harnessLogins", { input: {} });
}

type WriteName =
  | "save"
  | "saveLogin"
  | "delete"
  | "startHarnessSignIn"
  | "finishClaudeSignIn"
  | "checkCodexSignIn"
  | "removeHarnessLogin";

function useWrite<TInput, TOutput>(name: WriteName): MutationResult<TOutput, TInput> {
  const mutation = useContractMutation(vaultTrpcContract, name);
  const invalidate = useInvalidateContract();
  const typed = mutation as unknown as MutationResult<TOutput, TInput>;
  return {
    ...typed,
    run: (input: TInput) => typed.mutateAsync(input).finally(() => void invalidate(vaultTrpcContract)),
  };
}

export const useSaveVaultSecret = (): MutationResult<VaultSecret, SaveVaultSecretInput> => useWrite("save");
/** A website login (D657): its password and code key go in once, like any value. */
export const useSaveVaultLogin = (): MutationResult<VaultSecret, SaveVaultLoginInput> =>
  useWrite("saveLogin");
export const useDeleteVaultSecret = (): MutationResult<Ok, DeleteVaultSecretInput> => useWrite("delete");
export const useStartHarnessSignIn = (): MutationResult<HarnessSignIn, StartHarnessSignInInput> =>
  useWrite("startHarnessSignIn");
export const useFinishClaudeSignIn = (): MutationResult<HarnessLogin, FinishClaudeSignInInput> =>
  useWrite("finishClaudeSignIn");
export const useCheckCodexSignIn = (): MutationResult<CodexSignInCheck, CheckCodexSignInInput> =>
  useWrite("checkCodexSignIn");
export const useRemoveHarnessLogin = (): MutationResult<Ok, RemoveHarnessLoginInput> =>
  useWrite("removeHarnessLogin");
